/**
 * Management fee monthly sweep — dry-run and commit reports (API + web).
 * Shapes must stay aligned with `runManagementFeeSweep` in apps/api.
 */

/** Terminal outcome per property row (maps to dashboard coloring). */
export const MANAGEMENT_FEE_SWEEP_PROPERTY_STATUSES = [
  'success_posted',
  'success_idempotent',
  'skipped_no_fee_policy',
  'skipped_zero_fee',
  'skipped_dry_run',
  'error',
] as const;

export type ManagementFeeSweepPropertyStatus = (typeof MANAGEMENT_FEE_SWEEP_PROPERTY_STATUSES)[number];

/** Operating cash vs fee — informational only; accrual sweep does not block on low cash. */
export type ManagementFeeSweepLiquidityProjection = {
  spendableCents: number;
  rawBalanceCents: number;
  reserveCents: number;
  isReserveViolation: boolean;
  /** True when `feeCents > 0` and spendable operating cash covers the accrued fee. */
  isLikelyPayable: boolean;
};

export type ManagementFeeSweepPropertyRow = {
  propertyId: string;
  propertyName: string;
  /** Glanceable policy label, e.g. "8% Collected", "$200 flat / mo", "None". */
  policyLabel: string;
  /** Basis used for percent fees; null for flat monthly or when not applicable. */
  basisKind: 'COLLECTED' | 'BILLED' | 'FLAT_MONTHLY' | null;
  basisCents: number | null;
  feeCents: number;
  status: ManagementFeeSweepPropertyStatus;
  billId?: string;
  invoiceNumber?: string;
  /** Inactive policy reason or error message for operators. */
  detail?: string;
  liquidityProjection?: ManagementFeeSweepLiquidityProjection;
};

export type ManagementFeeSweepReport = {
  operatingOrganizationId: string;
  accrualPeriodYyyyMm: string;
  dryRun: boolean;
  periodInclusiveStart: string;
  periodInclusiveEnd: string;
  propertyCount: number;
  rows: ManagementFeeSweepPropertyRow[];
  /** Counts by terminal status for dashboards. */
  summary: {
    successPosted: number;
    successIdempotent: number;
    skippedNoFeePolicy: number;
    skippedZeroFee: number;
    skippedDryRun: number;
    errors: number;
  };
};

/** POST /accounting/management-fee-sweeps request body (aligned with API Zod schema). */
export type ManagementFeeSweepPostBody = {
  operatingOrganizationId: string;
  yyyyMm: string;
  dryRun: boolean;
};
